import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  effect,
  computed,
  ElementRef,
  Inject,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT, Location } from '@angular/common';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { IconsModule } from '@progress/kendo-angular-icons';
import { DropDownListComponent, DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { IndicatorsModule, LoaderModule } from '@progress/kendo-angular-indicators';
import { filter, forkJoin, switchMap, takeUntil, tap } from 'rxjs';
import { OrgAppbarComponent } from '../../../layout/components/org-appbar/org-appbar.component';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import {
  AccountTypeResult,
  AlertSyncControls,
  Ckb,
  IndustryResult,
  Organization,
  UserList,
} from '../../../../../core/models/entities';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationForm, SubdomainForm, CkbFormGrup } from '../../../../../core/models/forms';
import {
  GeneralOrganizationServiceCkbRequest,
  GeneralOrganizationService,
} from './service/general-organization.service';
import { UserService } from '../../../users/services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '../../../../../core/models/user';
import { CyflareAutocompleteComponent } from '../../../../../core/components/cyflare-autocomplete/cyflare-autocomplete.component';
import { SubscriptionsComponent } from './components/subscriptions/subscriptions.component';
import { MetricCardComponent } from '../../../../../core/components/metric-card/metric-card.component';
import {
  BillingBasis,
  BillingBasisSource,
  BillingService,
  MinimumCommitResponse,
  OrganizationBillingSettings,
} from '../../services/billing.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@progress/kendo-angular-notification';
import { ErrorHandlingService } from '../../../../../core/services/error-handling.service';

type CkbGroup = 'mxdr' | 'medr' | 'accountMappings';

@Component({
  selector: 'app-general',
  standalone: true,
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LoaderModule,
    LayoutModule,
    ChartsModule,
    FormsModule,
    ReactiveFormsModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    IconsModule,
    DropDownsModule,
    NavigationModule,
    IndicatorsModule,
    OrgAppbarComponent,
    CyflareAutocompleteComponent,
    MetricCardComponent,
  ],
  providers: [OrganizationService, GeneralOrganizationService],
})
export class GeneralComponent extends BaseComponent {
  public router = inject(Router);
  private route = inject(ActivatedRoute);
  public orgService = inject(OrganizationService);

  public userService = inject(UserService);

  public location = inject(Location);
  public generalOrgService = inject(GeneralOrganizationService);
  private billingService = inject(BillingService);
  private notification = inject(NotificationService);
  private errorHandlingService = inject(ErrorHandlingService);
  public user = inject(User);
  users = signal<UserList[]>([]);
  availableUsers = signal<UserList[]>([]);
  availableUsersFiltered = signal<UserList[]>([]);
  allUsers = signal<{ id: number; username: string }[]>([]);
  bodyElement: ElementRef;
  @ViewChild('userDropDownList') userDropDownList!: DropDownListComponent;
  accountTypeItems = signal<AccountTypeResult[]>([]);
  industryItems = signal<IndustryResult[]>([]);

  public orgForm = signal(this.getOrganizationForm());
  public ckbForm = signal(this.getCkbForm());

  public id = computed(() => this.orgForm().get('id')?.value ?? 0);
  public accountType = computed(() => this.orgForm().get('account_type')?.value ?? null);
  public isMspOrMmsp = computed(() => {
    const t = this.accountType();
    return t === 2 || t === 3;
  });
  public sentinelOneId = computed(() => this.ckbForm().get('sentinel_one_id')?.value ?? null);

  public minimumCommit = signal<MinimumCommitResponse | null>(null);
  public isMinimumCommitLoading = signal<boolean>(false);

  // Billing basis (B12): the resolved value + provenance, for the read-only hint under the dropdown.
  public billingBasisEffective = signal<BillingBasis | null>(null);
  public billingBasisSource = signal<BillingBasisSource | null>(null);
  public billingBasisInheritedFrom = signal<string | null>(null);
  // Concrete choices; the "Inherit from partner" (null) state is the dropdown's defaultItem.
  public readonly billingBasisOptions: { value: BillingBasis; label: string }[] = [
    { value: 'larger_of', label: 'Larger of contracted or actual' },
    { value: 'contracted', label: 'Contracted count' },
    { value: 'actual', label: 'Actual count' },
  ];
  public readonly billingBasisInheritItem = { value: null, label: 'Inherit from partner' };
  private readonly billingBasisLabels: Record<BillingBasis, string> = {
    larger_of: 'Larger of contracted or actual',
    contracted: 'Contracted count',
    actual: 'Actual count',
  };
  // The "currently: X" hint shown when this org inherits or uses the default.
  public billingBasisHint = computed(() => {
    const source = this.billingBasisSource();
    const effective = this.billingBasisEffective();
    if (!effective || source === 'self') return '';
    const label = this.billingBasisLabels[effective];
    if (source === 'inherited') {
      const from = this.billingBasisInheritedFrom();
      return `Inherited${from ? ` from ${from}` : ''}: ${label}`;
    }
    return `Default: ${label}`;
  });
  public isReadOnly = signal(true);
  public buttonMxdrText: string = 'Edit';
  public isReadOnlyGeneral = signal(true);
  public isReadOnlyMxdr = signal(true);
  public isReadOnlyMedr = signal(true);
  public isReadOnlyAccountMappings = signal(true);
  public groups = signal<string[]>([]);
  isAdmin: boolean = false;
  isCustomer: boolean = false;
  public isLoading = signal<boolean>(true);
  // Which alert-preference sync controls to show. Computed by the backend
  // (single source of truth) so the UI and the API never disagree.
  public alertSyncControls = signal<AlertSyncControls>({
    propagate_to_children: false,
    stop_parent_sync: false,
  });
  orgDefaultItem: any = { id: 0, display_text: 'Select Organization' };

  constructor(@Inject(DOCUMENT) private document: Document) {
    super();
    this.bodyElement = new ElementRef(this.document.body);
    this.initEffect();
  }

  ngOnInit() {
    this.loadAccountTypes();
    this.loadindustryItems();
  }
  private initEffect() {
    effect(
      () => {
        this.loadUserData();
        this.route.queryParams
          .pipe(
            filter((params) => params['organizationId']),
            switchMap((params) => {
              const orgId = +params['organizationId'];
              return forkJoin({
                organization: this.orgService.getOrganizationById(orgId),
                ckbData: this.generalOrgService.getCkbById(orgId),
                users: this.generalOrgService.listOrganizationUsers(orgId),
                orgId: this.orgService.getOrganizationById(orgId),
                accountTypes: this.generalOrgService.getAccountTypes(),
                industries: this.generalOrgService.getIndustries(),
              });
            })
          )
          .subscribe(({ organization, ckbData, users }) => {
            this.orgForm.set(this.getOrganizationForm(organization as Organization));
            this.alertSyncControls.set(
              organization.alert_sync_controls ?? { propagate_to_children: false, stop_parent_sync: false }
            );
            this.orgDefaultItem = {
              id: organization.parent?.id ?? 0,
              display_text: organization.parent?.name ?? 'Select Organization',
            };
            this.ckbForm.set(this.getCkbForm(ckbData));
            this.users.set(users);
            this.setInitialFormState();
            this.loadBillingSettings((organization as Organization).id!);
            if (this.isMspOrMmsp()) {
              this.loadMinimumCommit((organization as Organization).id!);
            }
            this.isLoading.set(false);
          });
      },
      { allowSignalWrites: true } // Enabling signal writes within this effect
    );
  }

  loadUserData(): void {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const groups = user.groups || [];

    this.isAdmin = groups.includes('admin');
    this.isCustomer = groups.includes('customer');
  }

  private loadAccountTypes() {
    this.generalOrgService
      .getAccountTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (accountTypes) => {
          this.accountTypeItems.set(accountTypes.results as AccountTypeResult[]);
        },
        error: (error) => {
          console.error('Error fetching account types:', error);
        },
      });
  }

  private loadBillingSettings(orgId: number): void {
    this.billingService
      .getBillingSettings(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          this.orgForm().patchValue({
            nfr: settings.nfr,
            premium_sla: settings.premium_sla,
            billing_basis: settings.billing_basis ?? null,
          });
          this.billingBasisEffective.set(settings.effective_billing_basis ?? null);
          this.billingBasisSource.set(settings.billing_basis_source ?? null);
          this.billingBasisInheritedFrom.set(settings.billing_basis_inherited_from ?? null);
        },
        error: (err) => console.error('Failed to load billing settings:', err),
      });
  }

  private loadMinimumCommit(orgId: number): void {
    this.isMinimumCommitLoading.set(true);
    this.billingService
      .getMinimumCommit(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.minimumCommit.set(res);
          this.isMinimumCommitLoading.set(false);
        },
        error: (err) => {
          console.error('Failed to load minimum commit:', err);
          this.isMinimumCommitLoading.set(false);
        },
      });
  }

  private loadindustryItems() {
    this.generalOrgService
      .getIndustries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (industries) => {
          this.industryItems.set(industries.results as IndustryResult[]);
        },
        error: (error) => {
          console.error('Error fetching industries:', error);
        },
      });
  }

  public loadOrgAvailableUsers(orgId: number) {
    this.generalOrgService
      .listAvailableOrganizationUsers(orgId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => {
          this.availableUsers.set(users as UserList[]);
          this.availableUsersFiltered.set(this.availableUsers());
        },
        error: (error) => {
          console.error('Error fetching organization available users:', error);
        },
      });
  }

  private setInitialFormState() {
    this.isReadOnlyGeneral.set(true);
    this.isReadOnlyMxdr.set(true);
    this.isReadOnlyMedr.set(true);
    this.isReadOnlyAccountMappings.set(true);

    this.toggleFormControls('general', true);
    this.toggleFormControls('mxdr', true);
    this.toggleFormControls('medr', true);
    this.toggleFormControls('accountMappings', true);
  }

  getOrganizationForm(organization = new Organization()): FormGroup<OrganizationForm> {
    return new FormGroup({
      subdomains: new FormArray<FormGroup<SubdomainForm>>(
        organization.subdomains?.map(
          (subdomain) => new FormGroup({ subdomain: new FormControl({ value: subdomain.subdomain!, disabled: true }) })
        ) ?? []
      ),
      tags: new FormArray<FormControl<number | null>>(
        organization.tags?.map((tag) => new FormControl({ value: tag, disabled: true })) ?? []
      ),
      name: new FormControl({ value: organization.name, disabled: true }, [Validators.required]),
      status: new FormControl({ value: organization.status, disabled: true }, [Validators.required]),
      account_type: new FormControl({ value: organization.account_type, disabled: true }, [Validators.required]),
      industry: new FormControl(
        {
          value: organization.industry ? (organization.industry as any).id : null, // Extract the ID
          disabled: true,
        },
        [Validators.required]
      ),
      owner: new FormControl({ value: organization.owner, disabled: true }, [Validators.required]),
      owner_email: new FormControl({ value: organization.owner_email, disabled: true }, [Validators.required]),
      do_not_call_client: new FormControl({ value: organization.do_not_call_client, disabled: true }, [
        Validators.required,
      ]),
      num_of_employee: new FormControl({ value: organization.num_of_employee, disabled: true }, [Validators.required]),
      website: new FormControl({ value: organization.website, disabled: true }, [Validators.required]),
      billing_street: new FormControl({ value: organization.billing_street, disabled: true }, [Validators.required]),
      billing_city: new FormControl({ value: organization.billing_city, disabled: true }, [Validators.required]),
      billing_country: new FormControl({ value: organization.billing_country, disabled: true }, [Validators.required]),
      billing_state: new FormControl({ value: organization.billing_state, disabled: true }, [Validators.required]),
      use_same_billing_address_for_shipping: new FormControl(
        { value: organization.use_same_billing_address_for_shipping, disabled: true },
        [Validators.required]
      ),
      shipping_street: new FormControl({ value: organization.shipping_street, disabled: true }, [Validators.required]),
      shipping_city: new FormControl({ value: organization.shipping_city, disabled: true }, [Validators.required]),
      shipping_country: new FormControl({ value: organization.shipping_country, disabled: true }, [
        Validators.required,
      ]),
      shipping_state: new FormControl({ value: organization.shipping_state, disabled: true }, [Validators.required]),
      account_manager_name: new FormControl({ value: organization.account_manager_name, disabled: true }, [
        Validators.required,
      ]),
      account_manager_phone: new FormControl({ value: organization.account_manager_phone, disabled: true }, [
        Validators.required,
      ]),
      account_manager_email: new FormControl({ value: organization.account_manager_email, disabled: true }, [
        Validators.required,
      ]),
      assigned_csm: new FormControl({ value: organization.assigned_csm, disabled: true }, [Validators.required]),
      assigned_csm_phone: new FormControl({ value: organization.assigned_csm_phone, disabled: true }, [
        Validators.required,
      ]),
      assigned_csm_email: new FormControl({ value: organization.assigned_csm_email, disabled: true }, [
        Validators.required,
      ]),
      soc_phone: new FormControl({ value: organization.soc_phone, disabled: true }, [Validators.required]),
      soc_escalation_email: new FormControl({ value: organization.soc_escalation_email, disabled: true }, [
        Validators.required,
      ]),
      soc_ir_email: new FormControl({ value: organization.soc_ir_email, disabled: true }, [Validators.required]),
      soc_subdomain: new FormControl({ value: organization.soc_subdomain, disabled: true }, [Validators.required]),
      id: new FormControl({ value: organization.id, disabled: true }, [Validators.required]),
      created_at: new FormControl(
        { value: organization.created_at && new Date(organization.created_at), disabled: true },
        [Validators.required]
      ),
      parent: new FormControl({ value: organization.parent?.name ?? '', disabled: true }, [Validators.required]),
      parentId: new FormControl({ value: organization.parent?.id ?? null, disabled: true }, [Validators.required]),
      is_demo_org: new FormControl({ value: organization.is_demo_org, disabled: true }, [Validators.required]),
      us_only: new FormControl({ value: organization.us_only, disabled: true }, [Validators.required]),
      propagate_alert_preferences_to_children: new FormControl<boolean | null | undefined>({
        value: organization.propagate_alert_preferences_to_children ?? false,
        disabled: true,
      }),
      stop_parent_synchronization: new FormControl<boolean | null | undefined>({
        value: organization.stop_parent_synchronization ?? false,
        disabled: true,
      }),
      nfr: new FormControl<boolean | null | undefined>({ value: false, disabled: true }),
      premium_sla: new FormControl<boolean | null | undefined>({ value: false, disabled: true }),
      billing_basis: new FormControl<string | null>({ value: null, disabled: true }),
    });
  }

  getCkbForm(ckb = new Ckb()): FormGroup<CkbFormGrup> {
    return new FormGroup({
      xdr_windows_agents: new FormControl({ value: ckb.xdr_windows_agents ?? '', disabled: true }, [
        Validators.required,
      ]),
      xdr_linux_agents: new FormControl({ value: ckb.xdr_linux_agents ?? '', disabled: true }, [Validators.required]),
      xdr_network_sensors: new FormControl({ value: ckb.xdr_network_sensors ?? '', disabled: true }, [
        Validators.required,
      ]),
      xdr_api_connectors: new FormControl({ value: ckb.xdr_api_connectors ?? '', disabled: true }, [
        Validators.required,
      ]),
      xdr_syslog_senders: new FormControl({ value: ckb.xdr_syslog_senders ?? '', disabled: true }, [
        Validators.required,
      ]),
      mdr_windows: new FormControl({ value: ckb.mdr_windows ?? '', disabled: true }, [Validators.required]),
      mdr_mac: new FormControl({ value: ckb.mdr_mac ?? '', disabled: true }, [Validators.required]),
      mdr_linux: new FormControl({ value: ckb.mdr_linux ?? '', disabled: true }, [Validators.required]),
      zoho_id: new FormControl({ value: ckb.zoho_id ?? '', disabled: true }, [Validators.required]),
      zoho_name: new FormControl({ value: ckb.zoho_name ?? '', disabled: true }, [Validators.required]),
      stellar_cyber_id: new FormControl({ value: ckb.stellar_cyber_id ?? '', disabled: true }, [Validators.required]),
      stellar_cyber_name: new FormControl({ value: ckb.stellar_cyber_name ?? '', disabled: true }, [
        Validators.required,
      ]),
      sentinel_one_id: new FormControl({ value: ckb.sentinel_one_id ?? '', disabled: true }, [Validators.required]),
      sentinel_one_name: new FormControl({ value: ckb.sentinel_one_name ?? '', disabled: true }, [Validators.required]),
      cyrisma_id: new FormControl({ value: ckb.cyrisma_id ?? '', disabled: true }, [Validators.required]),
      cyrisma_name: new FormControl({ value: ckb.cyrisma_name ?? '', disabled: true }, [Validators.required]),
      assure_id: new FormControl({ value: ckb.assure_id ?? '', disabled: true }, [Validators.required]),
      assure_name: new FormControl({ value: ckb.assure_name ?? '', disabled: true }, [Validators.required]),
      chronicle_id: new FormControl({ value: ckb.chronicle_id ?? '', disabled: true }, [Validators.required]),
      chronicle_name: new FormControl({ value: ckb.chronicle_name ?? '', disabled: true }, [Validators.required]),
      xrai_id: new FormControl({ value: ckb.xrai_id ?? '', disabled: true }),
      xrai_name: new FormControl({ value: ckb.xrai_name ?? '', disabled: true }),
      polymer_id: new FormControl({ value: ckb.polymer_id ?? '', disabled: true }, [Validators.required]),
      polymer_name: new FormControl({ value: ckb.polymer_name ?? '', disabled: true }, [Validators.required]),
      checkpoint_id: new FormControl({ value: ckb.checkpoint_id ?? '', disabled: true }, [Validators.required]),
      checkpoint_name: new FormControl({ value: ckb.checkpoint_name ?? '', disabled: true }, [Validators.required]),
      crowdstrike_edr_id: new FormControl({ value: ckb.crowdstrike_edr_id ?? '', disabled: true }, [
        Validators.required,
      ]),
      crowdstrike_edr_name: new FormControl({ value: ckb.crowdstrike_edr_name ?? '', disabled: true }, [
        Validators.required,
      ]),
      crowdstrike_xdr_id: new FormControl({ value: ckb.crowdstrike_xdr_id ?? '', disabled: true }, [
        Validators.required,
      ]),
      crowdstrike_xdr_name: new FormControl({ value: ckb.crowdstrike_xdr_name ?? '', disabled: true }, [
        Validators.required,
      ]),
      vicarius_id: new FormControl({ value: ckb.vicarius_id ?? '', disabled: true }, [Validators.required]),
      vicarius_name: new FormControl({ value: ckb.vicarius_name ?? '', disabled: true }, [Validators.required]),
      zenskar_id: new FormControl({ value: ckb.zenskar_id ?? '', disabled: true }),
      zenskar_name: new FormControl({ value: ckb.zenskar_name ?? '', disabled: true }),
    });
  }

  private toggleFormControls(group: 'general' | 'mxdr' | 'medr' | 'accountMappings', isReadOnly: boolean) {
    const controlsToToggle = () => {
      let general = [
        'name',
        'owner',
        'owner_email',
        'website',
        'billing_street',
        'billing_city',
        'billing_country',
        'billing_state',
        'use_same_billing_address_for_shipping',
        'shipping_street',
        'shipping_city',
        'shipping_country',
        'shipping_state',
        'account_manager_name',
        'account_manager_phone',
        'account_manager_email',
        'assigned_csm',
        'assigned_csm_phone',
        'assigned_csm_email',
        'soc_phone',
        'soc_escalation_email',
        'soc_ir_email',
        'soc_subdomain',
        'parentId',
        'industry',
        'is_demo_org',
        'us_only',
        'propagate_alert_preferences_to_children',
        'stop_parent_synchronization',
        'nfr',
        'premium_sla',
        'billing_basis',
      ];

      if (this.user.groups.includes('admin')) {
        general.push('account_type', 'status');
      }

      return {
        general: general,
        mxdr: [
          'xdr_windows_agents',
          'xdr_linux_agents',
          'xdr_network_sensors',
          'xdr_api_connectors',
          'xdr_syslog_senders',
        ],
        medr: ['mdr_windows', 'mdr_linux', 'mdr_mac'],
        accountMappings: [
          'zoho_id',
          'zoho_name',
          'stellar_cyber_id',
          'stellar_cyber_name',
          'sentinel_one_id',
          'sentinel_one_name',
          'cyrisma_id',
          'cyrisma_name',
          'assure_id',
          'assure_name',
          'chronicle_id',
          'chronicle_name',
          'xrai_id',
          'xrai_name',
          'polymer_id',
          'polymer_name',
          'connect_wise_id',
          'connect_wise_name',
          'checkpoint_id',
          'checkpoint_name',
          'crowdstrike_edr_id',
          'crowdstrike_edr_name',
          'crowdstrike_xdr_id',
          'crowdstrike_xdr_name',
          'vicarius_id',
          'vicarius_name',
          'zenskar_id',
          'zenskar_name',
        ],
      };
    };

    const orgForm = this.orgForm();
    const ckbForm = this.ckbForm();

    controlsToToggle()[group].forEach((control: string) => {
      let formControl = orgForm.get(control);
      if (!formControl) {
        formControl = ckbForm.get(control);
      }

      if (formControl) {
        if (isReadOnly) {
          formControl.disable();
        } else {
          formControl.enable();
        }
      }
    });
  }

  onButtonClickGeneral() {
    const isReadOnlyBefore = this.isReadOnlyGeneral();
    this.isReadOnlyGeneral.set(!isReadOnlyBefore);
    this.toggleFormControls('general', !isReadOnlyBefore);
    if (!isReadOnlyBefore) {
      this.saveOrgChanges();
    }
  }

  onButtonClickMxdr() {
    const isReadOnlyBefore = this.isReadOnlyMxdr();
    this.isReadOnlyMxdr.set(!isReadOnlyBefore);
    this.toggleFormControls('mxdr', !isReadOnlyBefore);
    if (!isReadOnlyBefore) {
      this.saveChanges('mxdr');
    }
  }

  onButtonClickMedr() {
    const isReadOnlyBefore = this.isReadOnlyMedr();
    this.isReadOnlyMedr.set(!isReadOnlyBefore);
    this.toggleFormControls('medr', !isReadOnlyBefore);

    if (!isReadOnlyBefore) {
      this.saveChanges('medr');
    }
  }

  onButtonClickAccountMappings() {
    const isReadOnlyBefore = this.isReadOnlyAccountMappings();
    this.isReadOnlyAccountMappings.set(!isReadOnlyBefore);
    this.toggleFormControls('accountMappings', !isReadOnlyBefore);
    if (!isReadOnlyBefore) {
      this.saveChanges('accountMappings');
    }
  }

  private saveOrgChanges() {
    // Save Organization Data
    this.toggleFormControls('general', false);
    const orgFormData = this.convertEmptyStringsToNull(this.orgForm().getRawValue());
    const billingSettings: OrganizationBillingSettings = {
      nfr: !!orgFormData.nfr,
      premium_sla: !!orgFormData.premium_sla,
      // null = inherit from parent; always sent so clearing an override persists.
      billing_basis: (orgFormData.billing_basis as BillingBasis | null) ?? null,
    };
    delete orgFormData.parent;
    delete orgFormData.nfr;
    delete orgFormData.premium_sla;
    delete orgFormData.billing_basis;
    this.toggleFormControls('general', true);
    const orgPayload: Organization = {
      id: this.id(),
      ...orgFormData,
      parent: { id: orgFormData.parentId, name: orgFormData.parent },
    };
    this.orgService
      .updateOrg(orgPayload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {});

    this.billingService
      .updateBillingSettings(this.id(), billingSettings)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => {
          // Refresh the resolved basis + provenance hint from the server's response.
          this.billingBasisEffective.set(settings.effective_billing_basis ?? null);
          this.billingBasisSource.set(settings.billing_basis_source ?? null);
          this.billingBasisInheritedFrom.set(settings.billing_basis_inherited_from ?? null);
        },
        error: (err) => console.error('Failed to save billing settings:', err),
      });
  }

  private saveChanges(group: CkbGroup) {
    const ckbFormData = this.convertEmptyStringsToNull(this.ckbForm().value);

    const payload: GeneralOrganizationServiceCkbRequest = {
      organization_id: this.id(),
      ...ckbFormData,
    };

    this.generalOrgService
      .updateCkbApi(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: (err: HttpErrorResponse) => this.showCkbSaveError(err, group) });
  }

  /** Field errors (e.g. a Stellar/S1 id already mapped elsewhere) reopen the section and show under the field. */
  private showCkbSaveError(err: HttpErrorResponse, group: CkbGroup) {
    const form = this.ckbForm();
    const body = err.status === 400 && err.error && typeof err.error === 'object' ? err.error : {};
    const fieldErrors = Object.entries(body).filter(([key]) => form.get(key));

    if (fieldErrors.length) {
      const readOnly = {
        mxdr: this.isReadOnlyMxdr,
        medr: this.isReadOnlyMedr,
        accountMappings: this.isReadOnlyAccountMappings,
      };
      readOnly[group].set(false);
      this.toggleFormControls(group, false);
      fieldErrors.forEach(([key, messages]) => form.get(key)?.setErrors({ server: [messages].flat().join(' ') }));
      return;
    }

    this.notification.show({
      content:
        err.error && typeof err.error === 'object'
          ? this.errorHandlingService.flattenErrorMessages(err.error)
          : 'Failed to save the changes. Please try again.',
      cssClass: 'error-notification',
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'center', vertical: 'top' },
      type: { style: 'error', icon: true },
    });
  }

  private convertEmptyStringsToNull(formData: any): any {
    const transformedData: any = {};
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        transformedData[key] = formData[key] === '' ? null : formData[key];
      }
    }
    return transformedData;
  }

  onUserAssigned($event: any) {
    const userId = $event ? $event.id : null;
    if (userId) {
      const orgId = this.id();
      this.assignUser(userId, orgId);
    }
  }

  public onUserDropDownFilterChange(value: any): void {
    if (value) {
      const filterTerm = value.toLowerCase();
      this.availableUsersFiltered.set(
        this.availableUsers().filter(
          (s) =>
            (s.first_name && s.first_name.toLowerCase().indexOf(filterTerm) !== -1) ||
            (s.last_name && s.last_name.toLowerCase().indexOf(filterTerm) !== -1)
        )
      );
    } else {
      this.loadOrgAvailableUsers(this.id());
    }
  }

  public onUserDropDownOpen(event: any) {
    this.loadOrgAvailableUsers(this.id());
  }

  public assignUser(userId: number, orgId: number): void {
    this.generalOrgService
      .assignUserToOrganization(orgId, userId)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.generalOrgService.listOrganizationUsers(orgId))
      )
      .subscribe((updatedUsers) => {
        this.users.set(updatedUsers);
        this.userDropDownList.reset();
      });
  }

  public removeUser(userId: number, orgId: number): void {
    this.generalOrgService
      .removeUserFromOrganization(orgId, userId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.users.set(this.users().filter((u) => u.id !== userId));
      });
  }
}
